import { DialogCloseEvent, DialogComponent } from "./dialog.js";
import { QuickViewDrawerOpenEvent, ThemeEvents } from "@theme/events";
import { CartUpdateEvent } from "@theme/events";

/**
 * @typedef {{ didError?: boolean, source?: string, sellingPlanId?: string }} CartUpdateData
 * @typedef {{ data?: CartUpdateData, resource?: unknown, sourceId?: string }} CartUpdateDetail
 * @typedef {Event & { detail?: CartUpdateDetail }} CartUpdateLikeEvent
 *
 * @typedef {{ html?: Document, productId?: string, newProduct?: { id?: string, url?: string } }} VariantUpdateData
 * @typedef {{ featured_media?: { id?: string | number } }} VariantResource
 * @typedef {{ data?: VariantUpdateData, resource?: VariantResource }} VariantUpdateDetail
 * @typedef {Event & { detail?: VariantUpdateDetail }} VariantUpdateLikeEvent
 *
 * @typedef {HTMLElement & { getSelectedVariantId?: () => string | number | null }} ProductCardApi
 *
 * @typedef {HTMLElement & { selectSubscription?: (event?: Event | null, forceEmit?: boolean) => void, selectOneTime?: () => void }} GRProductOptionsElement
 *
 * @typedef {HTMLElement & { select?: (slide: { id: string }, next?: unknown, options?: { animate?: boolean }) => void }} SlideshowComponentElement
 */

class QuickViewDrawer extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Document>} */
  #cachedPages = new Map();

  /** @type {Map<string, Promise<Document | null>>} */
  #pendingFetches = new Map();

  /** @type {HTMLElement | null} */
  #optionsElement = null;

  /** @type {string | null} */
  #currentProductUrl = null;

  /** @type {string} */
  #namespaceToken = "qv";

  /** @type {number} */
  #openRequestToken = 0;

  /** @type {boolean} */
  #isGiftBoxQuickView = false;

  /**
   * Recharge gift product (rc-gift tag, RET-280). The drawer takes the chrome
   * of the Custom Box Builder's Product Details drawer and renders the gift
   * PDP's content and purchase blocks instead of the regular product controls.
   * @type {boolean}
   */
  #isRechargeGift = false;

  /**
   * Tailwind width utilities on the dialog. They are `!important`, which no
   * unlayered stylesheet can beat, so gift mode removes them and sizes the
   * dialog in CSS instead.
   */
  static GIFT_REMOVED_DIALOG_CLASSES = [
    "max-w-[335px]!",
    "sm:max-w-[410px]!",
    "w-[410px]",
  ];

  /** @type {number} */
  #giftGalleryIndex = 0;

  /** @type {boolean} */
  #giftGalleryBound = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      QuickViewDrawerOpenEvent.eventName,
      this.#handleOpenEvent,
    );
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      QuickViewDrawerOpenEvent.eventName,
      this.#handleOpenEvent,
    );
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
    );
    this.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
    );
    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    this.#abortController?.abort();
  }

  /**
   * @param {Event} event
   */
  #handleOpenEvent = async (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;

    const card = target?.closest("product-card");
    if (!(card instanceof HTMLElement)) return;

    const token = ++this.#openRequestToken;

    // Track if this is a gift-box product
    this.#isGiftBoxQuickView = card.hasAttribute("data-is-gift-box");
    this.#isRechargeGift = card.hasAttribute("data-rc-gift");

    this.#resetGiftMode();
    this.#render(card);
    this.#applyGiftBoxStyles();

    await this.#mountProductControls(card);
    if (token !== this.#openRequestToken) return;

    this.showDialog();
    requestAnimationFrame(() => {
      if (token !== this.#openRequestToken) return;
      // A fresh open starts at the top, whatever the last open scrolled to.
      const body = this.querySelector(".quick-view-drawer__body");
      if (body instanceof HTMLElement) body.scrollTop = 0;
      this.#trackQuickViewViewed(card);
    });
  };

  #handleDialogClose = () => {
    window.grAnalytics?.trackQuickviewClosed();
  };

  #getQuickViewListContext(card) {
    return (
      card.dataset.listContext ||
      card.dataset.collectionList ||
      card.dataset.collectionUrl ||
      card.dataset.sourceContext ||
      undefined
    );
  }

  #getQuickViewProductName(card) {
    const titleLink = card.querySelector('[ref="productTitleLink"]');
    const text =
      titleLink?.textContent ||
      card.querySelector(".product-card__link .visually-hidden")?.textContent ||
      "";

    const trimmed = text.replace(/\s+/g, " ").trim();
    return trimmed || undefined;
  }

  #trackQuickViewViewed(card) {
    const productId = card.dataset.productId || undefined;
    const productName = this.#getQuickViewProductName(card);
    const listContext = this.#getQuickViewListContext(card);

    const products = [];
    if (productId || productName) {
      products.push({
        id: productId,
        name: productName,
      });
    }

    const detailContext =
      listContext || products.length
        ? {
            detail: {
              actionField: listContext ? { list: listContext } : undefined,
              products: products,
            },
          }
        : undefined;

    window.grAnalytics?.trackQuickviewViewed({
      product_id: productId,
      product_name: productName,
      ...(detailContext ? { ecommerce: detailContext } : {}),
    });
  }

  /**
   * After a successful one-time add-to-cart from THIS drawer, open the GR cart drawer.
   * Subscription adds are handled by `gift-selection-drawer`.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const detail = /** @type {CartUpdateLikeEvent} */ (event).detail;

    if (detail?.data?.didError) return;
    if (detail?.data?.source !== "product-form-component") return;

    // Subscription adds are dispatched on `document`, so they never pass the
    // containment check below. For gift-excluded products no gift drawer will
    // take over, so when this drawer is open, close it and show the cart.
    if (
      detail?.data?.sellingPlanId &&
      detail?.data?.gwpExcluded === true
    ) {
      if (this.refs.dialog instanceof HTMLDialogElement && this.refs.dialog.open) {
        this.closeDialog();
        document.dispatchEvent(new Event("gr-cart:open"));
      }
      return;
    }

    if (!(event.target instanceof HTMLElement)) return;
    if (!this.contains(event.target)) return;

    const sellingPlanId = detail?.data?.sellingPlanId;
    if (sellingPlanId) {
      this.closeDialog();
      return;
    }

    this.closeDialog();
    document.dispatchEvent(new Event("gr-cart:open"));
  };

  /**
   * Keep drawer UI in sync when the mounted `variant-picker` updates.
   * @param {Event} event
   */
  #handleVariantUpdate = async (event) => {
    const variantHost = this.refs.variantHost;
    if (!(variantHost instanceof HTMLElement)) return;
    if (!(event.target instanceof Node)) return;
    if (!variantHost.contains(event.target)) return;

    const detail = /** @type {VariantUpdateLikeEvent} */ (event).detail;
    const html = detail?.data?.html;
    const resource = detail?.resource;
    if (!(html instanceof Document)) return;

    this.#updateGalleryForVariant(resource);
    this.#updateContentsFromHtml(html);

    // Ensure the refreshed form is listening before options emits selling plan updates.
    await this.#refreshProductFormFromHtml(html);
    await this.#refreshProductOptionsFromHtml(html);

    // Variant-picker morphing can reintroduce conflicting ids/names; re-namespace.
    const picker = variantHost.querySelector("variant-picker");
    if (picker instanceof HTMLElement) this.#namespaceVariantPicker(picker);
    const optionsHost = this.refs.optionsHost;
    if (optionsHost instanceof HTMLElement) {
      const options = optionsHost.querySelector(
        "gr-product-options",
      );
      if (options instanceof HTMLElement) this.#namespaceIds(options);
    }

    const formHost = this.refs.formHost;
    if (formHost instanceof HTMLElement) {
      const form = formHost.querySelector("product-form-component");
      if (form instanceof HTMLElement) this.#namespaceIds(form);
    }
  };

  /**
   * Force `gr-product-options` to emit a SellingPlanUpdateEvent so the mounted
   * `product-form-component` receives the current selection.
   */
  #reEmitPurchaseSelectionForForm() {
    const optionsHost = this.refs.optionsHost;
    if (!(optionsHost instanceof HTMLElement)) return;

    const options = optionsHost.querySelector("gr-product-options");
    if (!(options instanceof HTMLElement)) return;

    const subscriptionSelected =
      options
        .querySelector('[ref="subscriptionOption"]')
        ?.getAttribute("data-selected") === "true";

    const optionsEl = /** @type {GRProductOptionsElement} */ (options);

    requestAnimationFrame(() => {
      if (subscriptionSelected) {
        optionsEl.selectSubscription?.(null, true);
      } else {
        optionsEl.selectOneTime?.();
      }
    });
  }

  /**
   * Fetches the product page content (with caching and deduplication)
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const cached = this.#cachedPages.get(productPageUrl);
    if (cached) return cached;

    const pending = this.#pendingFetches.get(productPageUrl);
    if (pending) return pending;

    const fetchPromise = this.#doFetch(productPageUrl);
    this.#pendingFetches.set(productPageUrl, fetchPromise);

    try {
      const result = await fetchPromise;
      if (result) {
        this.#cachedPages.set(productPageUrl, result);
      }
      return result;
    } finally {
      this.#pendingFetches.delete(productPageUrl);
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #doFetch(productPageUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, "text/html");
    } catch (error) {
      const err = /** @type {unknown} */ (error);

      if (err instanceof DOMException && err.name === "AbortError") {
        return null;
      }

      if (typeof err === "object" && err !== null && "name" in err) {
        const name = /** @type {{ name?: unknown }} */ (err).name;
        if (name === "AbortError") return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {HTMLElement} card
   */
  async #mountProductControls(card) {
    const productPageUrl = this.#getProductPageUrl(card);
    if (!productPageUrl) return;

    this.#currentProductUrl = productPageUrl;

    const html = await this.fetchProductPage(productPageUrl);
    if (!html) return;

    const isPDP = window.location.pathname.includes("/products/");
    this.#updateContentsFromHtml(html);

    // If ratings weren't populated from the card (e.g., product without rating
    // metafields), try to get them from the product page HTML
    this.#updateRatingsFromHtml(html);

    if (this.#isRechargeGift) {
      await this.#mountGiftPdp(html);
      return;
    }

    await this.#mountVariantPicker(html);

    // Skip product options (subscription/one-time selector) for gift-boxes
    if (!this.#isGiftBoxQuickView) {
      await this.#mountProductOptions(html, isPDP);
    }

    await this.#mountProductForm(html);

    // Options mount before the form; the form can miss the initial selling plan emit.
    this.#reEmitPurchaseSelectionForForm();
  }

  /**
   * Refresh `gr-product-options` from the latest PDP HTML.
   * Preserves selection and frequency when possible.
   * @param {Document} html
   */
  async #refreshProductOptionsFromHtml(html) {
    const host = this.refs.optionsHost;
    if (!(host instanceof HTMLElement)) return;

    const current = host.querySelector("gr-product-options");
    const wasSubscription =
      current
        ?.querySelector('[ref="subscriptionOption"]')
        ?.getAttribute("data-selected") === "true";
    const currentFrequencySelect = current?.querySelector(
      '[ref="frequencySelect"]',
    );
    const previousFrequency =
      currentFrequencySelect instanceof HTMLSelectElement
        ? currentFrequencySelect.value
        : null;

    const next = html.querySelector("gr-product-options");
    if (!next) return;

    const clone = /** @type {HTMLElement} */ (next.cloneNode(true));
    clone.id = "quick-view-product-options";
    this.#namespaceIds(clone);

    host.innerHTML = "";
    this.#optionsElement = clone;
    host.appendChild(clone);

    const tagName = clone.localName;
    await customElements.whenDefined(tagName);
    customElements.upgrade(clone);

    this.#applyQuickViewFrequencyOptionLabels(clone);

    const freqModal = clone.querySelector("gr-frequency-modal");
    if (freqModal) {
      await customElements.whenDefined("gr-frequency-modal");
      customElements.upgrade(freqModal);
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (wasSubscription) {
      const upgraded = /** @type {GRProductOptionsElement} */ (clone);
      upgraded.selectSubscription?.(null, true);

      const freqSelect = clone.querySelector('[ref="frequencySelect"]');
      if (previousFrequency && freqSelect instanceof HTMLSelectElement) {
        freqSelect.value = previousFrequency;
        freqSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else {
      const upgraded = /** @type {GRProductOptionsElement} */ (clone);
      upgraded.selectOneTime?.();
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {string | null}
   */
  #getProductPageUrl(card) {
    const titleLink = card.querySelector('[ref="productTitleLink"]');
    const cardLink = card.querySelector('[ref="productCardLink"]');

    let href = null;
    if (titleLink instanceof HTMLAnchorElement)
      href = titleLink.getAttribute("href");
    if (!href && cardLink instanceof HTMLAnchorElement)
      href = cardLink.getAttribute("href");
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);

      const cardApi = /** @type {ProductCardApi} */ (card);
      const selectedVariantId =
        cardApi.getSelectedVariantId?.() ||
        (card.querySelector(
          'input[type="radio"]:checked[data-variant-id]',
        ) instanceof HTMLInputElement
          ? /** @type {HTMLInputElement} */ (
              card.querySelector('input[type="radio"]:checked[data-variant-id]')
            ).dataset.variantId
          : null);

      if (selectedVariantId)
        url.searchParams.set("variant", String(selectedVariantId));
      return `${url.pathname}${url.search}`;
    } catch {
      return href;
    }
  }

  /**
   * @param {Document} html
   */
  async #mountVariantPicker(html) {
    const host = this.refs.variantHost;
    if (!(host instanceof HTMLElement)) return;

    host.innerHTML = "";

    const pickerEl = html.querySelector(
      "variant-picker.gr-variant-picker, variant-picker",
    );
    if (!pickerEl) return;

    const clone = /** @type {HTMLElement} */ (pickerEl.cloneNode(true));
    clone.id = "quick-view-variant-picker";

    // Prevent variant-picker from treating this as the real PDP and morphing main/URL.
    clone.removeAttribute("data-template-product-match");

    // Prevent id/name collisions with any existing PDP controls.
    this.#namespaceIds(clone);
    this.#namespaceVariantPicker(clone);

    host.appendChild(clone);

    await customElements.whenDefined("variant-picker");
    customElements.upgrade(clone);
  }

  /**
   * @param {Document} html
   * @param {boolean} [isPDP=false]
   */
  async #mountProductOptions(html, isPDP = false) {
    const host = this.refs.optionsHost;
    if (!(host instanceof HTMLElement)) return;

    host.innerHTML = "";
    this.#optionsElement = null;

    const optionsEl = html.querySelector("gr-product-options");
    if (!optionsEl) return;

    const clone = /** @type {HTMLElement} */ (optionsEl.cloneNode(true));
    clone.id = "quick-view-product-options";
    clone.setAttribute("data-quick-view", "true");

    // Prevent id collisions (e.g. frequency select id/label) with the PDP.
    this.#namespaceIds(clone);

    this.#optionsElement = clone;
    host.appendChild(clone);

    await customElements.whenDefined("gr-product-options");
    customElements.upgrade(clone);

    this.#applyQuickViewFrequencyOptionLabels(clone);

    const freqModal = clone.querySelector("gr-frequency-modal");
    if (freqModal) {
      await customElements.whenDefined("gr-frequency-modal");
      customElements.upgrade(freqModal);
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Intelligems cohort sync: rewrite every [data-selling-plan-id] in the
    // mounted options to use the cohort-duplicate's plan ids. Must run before
    // selectSubscription below so the initial SellingPlanUpdateEvent already
    // carries the cohort plan id (otherwise the form's hidden selling_plan
    // input briefly holds a control plan and the user's first ATC would 422).
    const igPlanSync = /** @type {any} */ (window).IGPlanSync;
    const controlHandle = this.#extractControlHandleFromCurrentUrl();
    if (igPlanSync?.remapSellingPlanIdsInScope && controlHandle) {
      try {
        await igPlanSync.remapSellingPlanIdsInScope(clone, controlHandle);
      } catch (err) {
        console.warn("[quick-view] IG plan remap failed (non-fatal):", err);
      }
    }

    const upgraded = /** @type {GRProductOptionsElement} */ (clone);
    upgraded.selectSubscription?.(null, true);
  }

  /**
   * Pulls the product handle out of `#currentProductUrl` (e.g.
   * `/products/ranchers-classic?variant=123` → `ranchers-classic`). Used to
   * key the Intelligems cohort plan-id remap.
   *
   * @returns {string | null}
   */
  #extractControlHandleFromCurrentUrl() {
    const url = this.#currentProductUrl;
    if (!url) return null;
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Quick View-only: show eater-frequency hints (3+, 2, 1) in the delivery frequency dropdown.
   * @param {HTMLElement} optionsRoot
   */
  #applyQuickViewFrequencyOptionLabels(optionsRoot) {
    const select = optionsRoot.querySelector('[ref="frequencySelect"]');
    if (!(select instanceof HTMLSelectElement)) return;

    const freqModal = optionsRoot.querySelector("gr-frequency-modal");
    if (!(freqModal instanceof HTMLElement)) return;

    /** @type {Map<string, string>} */
    const householdLabelByFrequency = new Map();
    for (const el of freqModal.querySelectorAll(
      "button[data-frequency][data-household-label]",
    )) {
      if (!(el instanceof HTMLButtonElement)) continue;
      const frequency = el.dataset.frequency;
      const label = el.dataset.householdLabel;
      if (!frequency || !label) continue;
      householdLabelByFrequency.set(frequency, label);
    }

    for (let idx = 0; idx < select.options.length; idx += 1) {
      const option = select.options.item(idx);
      if (!option) continue;
      const baseLabel = option.dataset.qvBaseLabel || option.textContent || "";
      option.dataset.qvBaseLabel = baseLabel;

      const suffix = householdLabelByFrequency.get(option.value) || null;
      if (!suffix) {
        option.textContent = baseLabel;
        continue;
      }

      option.textContent = `${baseLabel} (${suffix})`;
    }
  }

  /**
   * Toggle satisfaction guaranteed visibility (hidden for gift products,
   * whose purchase block carries its own satisfaction row)
   */
  #toggleSatisfactionGuarantee() {
    const regularSatisfaction = this.refs.regularSatisfactionGuarantee;
    if (regularSatisfaction instanceof HTMLElement) {
      regularSatisfaction.classList.toggle(
        "hidden",
        this.#isGiftBoxQuickView || this.#isRechargeGift,
      );
    }
  }

  /**
   * Clear the Recharge gift hosts and the gift dialog modifier so a regular
   * product opened after a gift (or the other way round) starts clean.
   */
  #resetGiftMode() {
    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLElement) {
      dialog.classList.toggle(
        "quick-view-drawer__dialog--gift",
        this.#isRechargeGift,
      );
      for (const cls of QuickViewDrawer.GIFT_REMOVED_DIALOG_CLASSES) {
        dialog.classList.toggle(cls, !this.#isRechargeGift);
      }
    }

    for (const ref of ["giftInfoHost", "giftPerksHost", "giftNoteHost"]) {
      const host = this.refs[ref];
      if (!(host instanceof HTMLElement)) continue;
      host.innerHTML = "";
      host.classList.add("hidden");
    }

    const track = this.refs.giftGalleryTrack;
    if (track instanceof HTMLElement) track.innerHTML = "";
    this.#giftGalleryIndex = 0;
    this.#updateGiftGalleryArrows();

    // #applyGiftBoxStyles hides these inline for gift boxes and never
    // restores them; gift products link through to their own PDP.
    if (!this.#isGiftBoxQuickView) {
      const viewDetailsWrapper = this.refs.viewDetailsWrapper;
      if (viewDetailsWrapper instanceof HTMLElement) {
        viewDetailsWrapper.style.removeProperty("display");
      }
      const regularSatisfaction = this.refs.regularSatisfactionGuarantee;
      if (regularSatisfaction instanceof HTMLElement) {
        regularSatisfaction.style.removeProperty("display");
      }
    }
  }

  /**
   * Recharge gift product: mount the gift PDP's blocks. Content blocks
   * (Box: Info, Box: Contents, gallery) already resolve to the source
   * product on the PDP, so the drawer shows the real product's content while
   * the purchase block stays on the Recharge gift product. The whole Box:
   * Purchase block is cloned as one unit so its Alpine price line keeps
   * listening to the variant picker radios, and the product form (with the
   * gift recipient inputs and teleported recipient drawer) hears the picker's
   * variant updates through the shared dialog.
   * @param {Document} html
   */
  async #mountGiftPdp(html) {
    const { variantHost, optionsHost, formHost, formHostFooter } = this.refs;

    for (const host of [variantHost, optionsHost, formHost, formHostFooter]) {
      if (host instanceof HTMLElement) host.innerHTML = "";
    }
    if (formHost instanceof HTMLElement) formHost.classList.add("hidden");
    if (formHostFooter instanceof HTMLElement) {
      formHostFooter.classList.add("hidden");
    }
    this.#optionsElement = null;
    this.#toggleSatisfactionGuarantee();

    this.#mountGiftGallery(html);

    // Spec line, summary and badges from Box: Info (title and reviews already
    // have their own hosts).
    const giftInfoHost = this.refs.giftInfoHost;
    const info = html.querySelector(".gr-box-info-card");
    if (giftInfoHost instanceof HTMLElement && info) {
      const parts = info.querySelectorAll(
        ".gr-box-info__spec, .gr-box-info__badges, .gr-box-info__summary",
      );
      for (const part of parts) giftInfoHost.appendChild(part.cloneNode(true));
      giftInfoHost.classList.toggle("hidden", parts.length === 0);
    }

    // "In the box" from Box: Contents, heading included, in place of the
    // card-style bullets.
    const contentsHost = this.refs.contentsHost;
    const contents = html.querySelector(".gr-box-contents-container");
    if (contentsHost instanceof HTMLElement && contents) {
      contentsHost.innerHTML = "";
      contentsHost.appendChild(contents.cloneNode(true));
    }

    // Box: Purchase (variant picker, price line, description, satisfaction
    // row) stays one unit so its Alpine price line keeps hearing the picker
    // radios. The product form moves to the sticky footer; it listens for
    // variant updates on the shared dialog, so the picker still reaches it.
    const purchase = html.querySelector(".gr-box-purchase");
    if (variantHost instanceof HTMLElement && purchase) {
      const clone = /** @type {HTMLElement} */ (purchase.cloneNode(true));
      this.#stripBlockStyles(clone);

      const picker = clone.querySelector("variant-picker");
      if (picker instanceof HTMLElement) {
        // Prevent variant-picker from treating this as the real PDP and morphing main/URL.
        picker.removeAttribute("data-template-product-match");
        this.#namespaceVariantPicker(picker);
      }

      const form = clone.querySelector("product-form-component");
      if (form instanceof HTMLElement && formHostFooter instanceof HTMLElement) {
        form.id = "quick-view-product-form";
        form.setAttribute("data-cart-source-context", "quick-view");
        this.#namespaceIds(form);
        formHostFooter.appendChild(form);
        formHostFooter.classList.remove("hidden");
      }

      this.#namespaceIds(clone);
      variantHost.appendChild(clone);

      await customElements.whenDefined("variant-picker");
      await customElements.whenDefined("product-form-component");
      customElements.upgrade(clone);
      if (form instanceof HTMLElement) customElements.upgrade(form);
    }

    this.#mountGiftCard(
      this.refs.giftPerksHost,
      html.querySelector(".gr-box-perks-card"),
    );
    this.#mountGiftCard(
      this.refs.giftNoteHost,
      html.querySelector(".gr-box-gift-note-card"),
    );
  }

  /**
   * Clone a settings-driven gift PDP card (perks, gift note) into its host.
   * @param {unknown} host
   * @param {Element | null} card
   */
  #mountGiftCard(host, card) {
    if (!(host instanceof HTMLElement)) return;
    host.innerHTML = "";
    if (!card) {
      host.classList.add("hidden");
      return;
    }
    const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
    this.#stripBlockStyles(clone);
    clone.removeAttribute("id");
    host.appendChild(clone);
    host.classList.remove("hidden");
  }

  /**
   * Drop a cloned block's own <style> tag. Those rules are keyed to the
   * block id and switch to the desktop layout on viewport width; the drawer
   * stylesheet restyles the cards for the panel instead.
   * @param {HTMLElement} root
   */
  #stripBlockStyles(root) {
    for (const style of root.querySelectorAll(":scope > style")) {
      style.remove();
    }
  }

  /**
   * Full-width snap carousel of the gift PDP gallery (the source product's
   * images), like the builder's Product Details drawer.
   * @param {Document} html
   */
  #mountGiftGallery(html) {
    const track = this.refs.giftGalleryTrack;
    if (!(track instanceof HTMLElement)) return;
    track.innerHTML = "";

    /** @type {Map<string, HTMLImageElement>} */
    const unique = new Map();
    const candidates = html.querySelectorAll(
      'gr-product-gallery img[ref^="desktopSlides"], gr-product-gallery img',
    );
    for (const img of candidates) {
      if (!(img instanceof HTMLImageElement)) continue;
      const src = img.getAttribute("src") || "";
      if (!src) continue;
      const key = src.split("?")[0];
      if (!unique.has(key)) unique.set(key, img);
    }

    for (const source of unique.values()) {
      const img = document.createElement("img");
      img.src = source.getAttribute("src") || "";
      const srcset = source.getAttribute("srcset");
      if (srcset) img.srcset = srcset;
      img.sizes = "(min-width: 768px) 700px, 100vw";
      img.alt = source.getAttribute("alt") || "";
      img.loading = "lazy";
      img.width = 700;
      img.height = 467;
      track.appendChild(img);
    }

    if (!this.#giftGalleryBound) {
      this.#giftGalleryBound = true;
      let frame = 0;
      track.addEventListener("scroll", () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = track.clientWidth || 1;
          this.#giftGalleryIndex = Math.round(track.scrollLeft / width);
          this.#updateGiftGalleryArrows();
        });
      });
    }

    this.#giftGalleryIndex = 0;
    track.scrollTo({ left: 0 });
    this.#updateGiftGalleryArrows();
  }

  #giftGalleryCount() {
    const track = this.refs.giftGalleryTrack;
    return track instanceof HTMLElement ? track.children.length : 0;
  }

  #updateGiftGalleryArrows() {
    const count = this.#giftGalleryCount();
    const prev = this.refs.giftGalleryPrev;
    const next = this.refs.giftGalleryNext;
    if (prev instanceof HTMLElement) {
      prev.hidden = count < 2 || this.#giftGalleryIndex <= 0;
    }
    if (next instanceof HTMLElement) {
      next.hidden = count < 2 || this.#giftGalleryIndex >= count - 1;
    }
  }

  /** @param {number} index */
  #scrollGiftGalleryTo(index) {
    const track = this.refs.giftGalleryTrack;
    if (!(track instanceof HTMLElement)) return;
    const count = this.#giftGalleryCount();
    const clamped = Math.max(0, Math.min(index, count - 1));
    this.#giftGalleryIndex = clamped;
    track.scrollTo({ left: clamped * track.clientWidth, behavior: "smooth" });
    this.#updateGiftGalleryArrows();
  }

  giftGalleryPrev() {
    this.#scrollGiftGalleryTo(this.#giftGalleryIndex - 1);
  }

  giftGalleryNext() {
    this.#scrollGiftGalleryTo(this.#giftGalleryIndex + 1);
  }

  /**
   * Mount PDP add-to-cart form so cart behavior matches product pages.
   * @param {Document} html
   */
  async #mountProductForm(html) {
    const host = this.refs.formHost;
    const footerHost = this.refs.formHostFooter;
    if (!(host instanceof HTMLElement)) return;
    if (!(footerHost instanceof HTMLElement)) return;

    host.innerHTML = "";
    footerHost.innerHTML = "";

    // Gift-box case: mount simple form in footer
    if (this.#isGiftBoxQuickView) {
      host.classList.add("hidden");
      footerHost.classList.remove("hidden");
      this.#toggleSatisfactionGuarantee();

      const formEl = html.querySelector(
        "product-form-component.gr-add-to-cart__product-form, product-form-component",
      );
      if (!formEl) return;

      const clone = /** @type {HTMLElement} */ (formEl.cloneNode(true));
      clone.id = "quick-view-product-form";
      clone.setAttribute("data-cart-source-context", "quick-view");
      this.#namespaceIds(clone);
      footerHost.appendChild(clone);

      await customElements.whenDefined("product-form-component");
      customElements.upgrade(clone);
      return;
    }

    // Regular product case: mount form in body
    host.classList.remove("hidden");
    footerHost.classList.add("hidden");
    this.#toggleSatisfactionGuarantee();

    const formEl = html.querySelector(
      "product-form-component.gr-add-to-cart__product-form, product-form-component",
    );
    if (!formEl) return;

    const clone = /** @type {HTMLElement} */ (formEl.cloneNode(true));
    clone.id = "quick-view-product-form";
    clone.setAttribute("data-cart-source-context", "quick-view");
    clone.classList.remove("hidden"); // add this line
    this.#namespaceIds(clone);

    host.appendChild(clone);

    await customElements.whenDefined("product-form-component");
    customElements.upgrade(clone);

    // Upgrade add-to-cart-component so it shows the button
    const addToCart = clone.querySelector("add-to-cart-component");
    if (addToCart instanceof HTMLElement) {
      await customElements.whenDefined("add-to-cart-component");
      customElements.upgrade(addToCart);
    }

    const btn = clone.querySelector('[ref="addToCartButton"]');
    if (btn instanceof HTMLElement) {
      btn.classList.remove("hidden");
      btn.className =
        "custom-button custom-button--primary gr-flex flex-row justify-center items-center px-6 py-2.5 gap-2 mt-3 h-[58px] w-full rounded font-ringside-ssm-bold! text-base capitalize transition-colors duration-200 locked-addons-cta cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden";
    }
    this.#reEmitPurchaseSelectionForForm();
  }

  /**
   * Refresh mounted product form from the latest variant HTML.
   * @param {Document} html
   */
  async #refreshProductFormFromHtml(html) {
    const host = this.refs.formHost;
    if (!(host instanceof HTMLElement)) return;
    if (!host.querySelector("product-form-component")) return;
    await this.#mountProductForm(html);
  }

  /**
   * Namespace ids inside a cloned fragment so `id`/`for` don't collide with the page.
   * @param {HTMLElement} root
   */
  #namespaceIds(root) {
    const token = `${this.#namespaceToken}-${this.id || "drawer"}`;
    /** @type {Map<string, string>} */
    const idMap = new Map();

    for (const el of root.querySelectorAll("[id]")) {
      if (!(el instanceof HTMLElement)) continue;
      const id = el.id;
      if (!id) continue;
      const next = `${id}--${token}`;
      idMap.set(id, next);
      el.id = next;
    }

    for (const label of root.querySelectorAll("label[for]")) {
      if (!(label instanceof HTMLLabelElement)) continue;
      const current = label.htmlFor;
      const next = idMap.get(current);
      if (next) label.htmlFor = next;
    }

    // Handle simple hash links if present.
    for (const a of root.querySelectorAll('a[href^="#"]')) {
      if (!(a instanceof HTMLAnchorElement)) continue;
      const href = a.getAttribute("href") || "";
      const raw = href.slice(1);
      const next = idMap.get(raw);
      if (next) a.setAttribute("href", `#${next}`);
    }
  }

  /**
   * Namespace radio `name`s to avoid cross-document grouping.
   * @param {HTMLElement} root
   */
  #namespaceVariantPicker(root) {
    const token = `${this.#namespaceToken}-${this.id || "drawer"}`;

    for (const input of root.querySelectorAll('input[type="radio"][name]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const name = input.name;
      if (!name) continue;
      if (name.includes(`--${token}`)) continue;
      input.name = `${name}--${token}`;
    }
  }

  /** @param {VariantResource | null | undefined} resource */
  #updateGalleryForVariant(resource) {
    const featuredMediaId = resource?.featured_media?.id;
    if (!featuredMediaId) return;

    const galleryHost = this.refs.galleryHost;
    if (!(galleryHost instanceof HTMLElement)) return;

    const mediaId = String(featuredMediaId);

    const slides = galleryHost.querySelectorAll(
      "slideshow-slide[variant-image]",
    );
    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;
      slide.hidden = slide.getAttribute("slide-id") !== mediaId;
    }

    const slideshow = /** @type {SlideshowComponentElement | null} */ (
      galleryHost.querySelector("slideshow-component")
    );
    slideshow?.select?.({ id: mediaId }, undefined, { animate: false });
  }

  /** @param {Document} html */
  #updateContentsFromHtml(html) {
    const contentsHost = this.refs.contentsHost;
    if (!(contentsHost instanceof HTMLElement)) return;

    // Source of truth for box contents: GR Value Props "WHAT'S IN THE BOX" accordion.
    /** @type {HTMLElement | null} */
    let details = /** @type {HTMLElement | null} */ (
      html.querySelector('details[data-block-id="gr-accordion-whats-in-box"]')
    );

    if (!details) {
      for (const el of Array.from(
        html.querySelectorAll("details.gr-product-accordion"),
      )) {
        const heading = el.querySelector('[role="heading"]')?.textContent || "";
        if (
          heading.replace(/\s+/g, " ").trim().toUpperCase() ===
          "WHAT'S IN THE BOX"
        ) {
          details = /** @type {HTMLElement} */ (el);
          break;
        }
      }
    }

    /** @type {string[]} */
    let items = [];

    if (details) {
      items = Array.from(details.querySelectorAll("ul li p"))
        .map((p) => (p.textContent || "").replace(/\s+/g, " ").trim())
        .filter(Boolean);
    } else {
      // Cuts hero PDPs (cut and gift box templates) carry the list in the
      // "In the box" card instead of the accordion.
      items = Array.from(html.querySelectorAll(".gr-box-contents__list li"))
        .map((li) => (li.textContent || "").replace(/\s+/g, " ").trim())
        .filter(Boolean);
    }

    if (!items.length) return;

    const wrapper = document.createElement("div");
    wrapper.className = "box-contents-wrapper";

    const ul = document.createElement("ul");

    for (const text of items) {
      const li = document.createElement("li");
      const bullet = document.createElement("span");
      bullet.textContent = "•";
      const body = document.createElement("span");
      body.textContent = text;
      li.appendChild(bullet);
      li.appendChild(body);
      ul.appendChild(li);
    }

    wrapper.appendChild(ul);
    contentsHost.innerHTML = "";
    contentsHost.appendChild(wrapper);
  }

  /**
   * Populate ratings from product page HTML if not already rendered from the card.
   * This handles products where the rating metafields may not be set.
   * @param {Document} html
   */
  #updateRatingsFromHtml(html) {
    const ratingsHost = this.refs.ratingsHost;
    if (!(ratingsHost instanceof HTMLElement)) return;

    // Skip if ratings were already populated from the card
    if (ratingsHost.children.length > 0) {
      console.log("[QuickView] Ratings already populated from card");
      return;
    }

    // Look for a reviews block in the PDP HTML: the card component on the
    // default PDP, or the cut / gift PDP component on the cuts hero.
    const reviewsBlock = html.querySelector(
      '[x-data^="productCardReviews"], [x-data^="cutReviews"]',
    );
    console.log("[QuickView] reviewsBlock found:", !!reviewsBlock);

    // Live rating from the SKU list wins; the static parsing below only runs
    // for pages with no reviews component at all.
    if (this.#mountLiveRatings(ratingsHost, this.#parseReviewsArgs(reviewsBlock))) {
      return;
    }

    let rating = 0;
    let count = 0;

    if (reviewsBlock) {
      // Extract rating data from the x-data attribute
      // Format: productCardReviews('SKU', 4.5, 123)
      const xData = reviewsBlock.getAttribute("x-data") || "";
      console.log("[QuickView] x-data:", xData);
      const match = xData.match(
        /productCardReviews\s*\(\s*['"][^'"]*['"]\s*,\s*([\d.]+)\s*,\s*(\d+)\s*\)/,
      );
      console.log("[QuickView] regex match:", match);
      if (match && match[1] && match[2]) {
        rating = parseFloat(match[1]);
        count = parseInt(match[2], 10);
      }
    }

    // If no Alpine block found, try looking in noscript fallback
    if ((!rating || !count) && html.querySelector("noscript")) {
      const noscript = html.querySelector(
        "noscript .product-card-reviews-stars",
      );
      console.log("[QuickView] noscript stars found:", !!noscript);
      if (noscript) {
        const ariaLabel = noscript.getAttribute("aria-label") || "";
        const ratingMatch = ariaLabel.match(/([\d.]+)\s+out\s+of\s+5/i);
        if (ratingMatch && ratingMatch[1]) {
          rating = parseFloat(ratingMatch[1]);
        }
        const countEl =
          noscript.parentElement?.querySelector("span:last-child");
        const countText = countEl?.textContent || "";
        const countMatch = countText.match(/\((\d+)\)/);
        if (countMatch && countMatch[1]) {
          count = parseInt(countMatch[1], 10);
        }
      }
    }

    // Also try static fallback (non-Alpine version)
    if (!rating || !count) {
      const staticReviews = html.querySelector(
        ".product-card-reviews-stars[aria-label]",
      );
      console.log("[QuickView] static reviews found:", !!staticReviews);
      if (staticReviews) {
        const ariaLabel = staticReviews.getAttribute("aria-label") || "";
        const ratingMatch = ariaLabel.match(/([\d.]+)\s+out\s+of\s+5/i);
        if (ratingMatch && ratingMatch[1]) {
          rating = parseFloat(ratingMatch[1]);
        }
        const countEl =
          staticReviews.parentElement?.querySelector("span:last-child");
        const countText = countEl?.textContent || "";
        const countMatch = countText.match(/\((\d+)\)/);
        if (countMatch && countMatch[1]) {
          count = parseInt(countMatch[1], 10);
        }
      }
    }

    console.log("[QuickView] Final rating:", rating, "count:", count);
    // Render ratings even if 0 - let the UI show empty stars / (0) count

    // Calculate star breakdown
    const fullStars = Math.floor(rating);
    const decimal = rating - fullStars;
    let hasHalfStar = false;
    let hasExtraFull = false;

    if (decimal >= 0.6) {
      hasExtraFull = true;
    } else if (decimal >= 0.3) {
      hasHalfStar = true;
    }

    let totalFull = fullStars;
    if (hasExtraFull) {
      totalFull = Math.min(fullStars + 1, 5);
    }

    let remainingAfterFull = 5 - totalFull;
    let emptyStars;
    if (hasHalfStar && totalFull < 5) {
      emptyStars = remainingAfterFull - 1;
      emptyStars = Math.max(0, emptyStars);
    } else {
      emptyStars = remainingAfterFull;
      hasHalfStar = false;
    }

    // Build the ratings display
    const wrapper = document.createElement("div");
    wrapper.className = "!flex !items-center !gap-1";

    const starsContainer = document.createElement("div");
    starsContainer.className = "product-card-reviews-stars !flex !items-center";
    starsContainer.setAttribute("role", "img");
    starsContainer.setAttribute("aria-label", `${rating} out of 5 stars`);

    // Get star SVG templates from the page
    const starClasses =
      "!w-4 !h-4 lg:!w-[18px] lg:!h-[18px] [&_svg]:!w-full [&_svg]:!h-full";

    // Try to get SVG content from template tags in the Alpine-based reviews
    // Templates contain the actual star markup
    let fullStarHtml = "";
    let halfStarHtml = "";
    let emptyStarHtml = "";

    // Look in Alpine template tags
    const templates = html.querySelectorAll(
      '[x-data^="productCardReviews"] template',
    );
    for (const template of templates) {
      const content = /** @type {HTMLTemplateElement} */ (template).content;
      const span = content.querySelector("span");
      if (!span) continue;

      const xFor = template.getAttribute("x-for") || "";
      const xIf = template.getAttribute("x-if") || "";

      if (xFor.includes("fullStars")) {
        fullStarHtml = span.innerHTML;
      } else if (xIf.includes("hasHalfStar")) {
        halfStarHtml = span.innerHTML;
      } else if (xFor.includes("emptyStars")) {
        emptyStarHtml = span.innerHTML;
      }
    }

    // Fallback: look in noscript or static fallback sections
    if (!fullStarHtml) {
      const noscriptStars = html.querySelector(
        "noscript .product-card-reviews-stars span",
      );
      fullStarHtml = noscriptStars?.innerHTML || "";
    }
    if (!fullStarHtml) {
      const staticStars = html.querySelector(
        ".product-card-reviews-stars span",
      );
      fullStarHtml = staticStars?.innerHTML || "";
    }

    // Final fallback to text symbols
    if (!fullStarHtml) fullStarHtml = "★";
    if (!halfStarHtml) halfStarHtml = fullStarHtml; // Use full star as fallback for half
    if (!emptyStarHtml) emptyStarHtml = "☆";

    // Add full stars
    for (let i = 0; i < totalFull; i++) {
      const span = document.createElement("span");
      span.className = starClasses;
      span.setAttribute("aria-hidden", "true");
      span.innerHTML = fullStarHtml;
      starsContainer.appendChild(span);
    }

    // Add half star if applicable
    if (hasHalfStar) {
      const span = document.createElement("span");
      span.className = starClasses;
      span.setAttribute("aria-hidden", "true");
      span.innerHTML = halfStarHtml;
      starsContainer.appendChild(span);
    }

    // Add empty stars
    for (let i = 0; i < emptyStars; i++) {
      const span = document.createElement("span");
      span.className = starClasses;
      span.setAttribute("aria-hidden", "true");
      span.innerHTML = emptyStarHtml;
      starsContainer.appendChild(span);
    }

    wrapper.appendChild(starsContainer);

    // Add the count
    const countSpan = document.createElement("span");
    countSpan.className = "!text-sm !text-gray-1100";
    countSpan.textContent = `(${count})`;
    wrapper.appendChild(countSpan);

    ratingsHost.appendChild(wrapper);
  }

  /**
   * @param {HTMLElement} card
   */
  #render(card) {
    const galleryHost = this.refs.galleryHost;
    const pricingHost = this.refs.pricingHost;
    const titleHost = this.refs.titleHost;
    const priceHost = this.refs.priceHost;
    const ratingsHost = this.refs.ratingsHost;
    const proteinsHost = this.refs.proteinsHost;
    const contentsHost = this.refs.contentsHost;
    if (!(galleryHost instanceof HTMLElement)) return;
    if (!(pricingHost instanceof HTMLElement)) return;
    if (!(titleHost instanceof HTMLElement)) return;
    if (!(priceHost instanceof HTMLElement)) return;
    if (!(ratingsHost instanceof HTMLElement)) return;
    if (!(proteinsHost instanceof HTMLElement)) return;
    if (!(contentsHost instanceof HTMLElement)) return;

    galleryHost.innerHTML = "";
    titleHost.innerHTML = "";
    priceHost.innerHTML = "";
    ratingsHost.innerHTML = "";
    proteinsHost.innerHTML = "";
    contentsHost.innerHTML = "";

    const gallery = card.querySelector(".card-gallery");
    const title = card.querySelector('[ref="productTitleLink"]');
    const pricing = card.querySelector("product-price");
    const ratings = card.querySelector('[x-data^="productCardReviews"]');
    const proteins = card.querySelector(".protein-pills");
    const contents = card.querySelector(".box-contents-wrapper");

    if (gallery) {
      const galleryClone = /** @type {HTMLElement} */ (gallery.cloneNode(true));
      galleryHost.append(galleryClone);

      // Upgrade slideshow components so they become interactive
      const slideshow = galleryClone.querySelector("slideshow-component");
      if (slideshow) {
        customElements.whenDefined("slideshow-component").then(() => {
          customElements.upgrade(slideshow);
        });
      }
    }
    this.#renderTitle(titleHost, title, card);
    // Gift products show the purchase block's price line instead.
    if (!this.#isRechargeGift) this.#renderPrice(priceHost, pricing);
    if (ratings) this.#renderRatings(ratingsHost, ratings);
    if (proteins) proteinsHost.append(proteins.cloneNode(true));
    if (contents) contentsHost.append(contents.cloneNode(true));
  }

  /**
   * Render price
   * @param {HTMLElement} priceHost
   * @param {Element | null} pricing
   */
  #renderPrice(priceHost, pricing) {
    if (!pricing) return;

    const salePriceEl = pricing.querySelector(".price");
    const comparePriceEl = pricing.querySelector(".compare-at-price");

    const salePrice = salePriceEl?.textContent?.trim() || "";
    const comparePrice = comparePriceEl?.textContent?.trim() || "";

    const priceContainer = pricing.querySelector('[ref="priceContainer"]');
    const productId =
      pricing.dataset?.productId || priceContainer?.dataset?.productId || "";
    const variantId =
      priceContainer?.dataset?.variantId || pricing.dataset?.variantId || "";

    const priceWrapper = document.createElement("div");
    priceWrapper.className = "gr-flex flex-row items-center gap-2 text-right";
    priceWrapper.setAttribute("data-testid", "product-price");
    if (productId) priceWrapper.setAttribute("data-product-id", productId);
    if (variantId) priceWrapper.setAttribute("data-variant-id", variantId);

    const salePriceDiv = document.createElement("div");
    salePriceDiv.className =
      "font-knockout-71 text-[28px] leading-[32px] text-black-warm igComparePrice";
    salePriceDiv.textContent = salePrice;
    priceWrapper.appendChild(salePriceDiv);

    if (comparePrice && comparePrice !== salePrice) {
      const comparePriceDiv = document.createElement("div");
      comparePriceDiv.className = "text-gray-900 line-through text-md igPrice";
      comparePriceDiv.textContent = comparePrice;
      priceWrapper.appendChild(comparePriceDiv);
    }

    priceHost.appendChild(priceWrapper);
  }

  /**
   * Render star ratings from product card reviews component
   * @param {HTMLElement} ratingsHost
   * @param {Element} ratingsEl
   */
  /**
   * Read the arguments of a reviews component's x-data expression.
   * Both the card component (`productCardReviews`) and the cut / gift PDP
   * component (`cutReviews`) take (skuList, fallbackRating, fallbackCount).
   * @param {Element | null} el
   * @returns {{ sku: string, rating: number, count: number } | null}
   */
  #parseReviewsArgs(el) {
    const xData = el?.getAttribute("x-data") || "";
    const match = xData.match(
      /^(?:productCardReviews|cutReviews)\s*\(\s*(['"])(.*?)\1\s*,\s*([\d.]+)\s*,\s*(\d+)\s*\)/s,
    );
    if (!match) return null;
    return {
      sku: match[2],
      rating: parseFloat(match[3]) || 0,
      count: parseInt(match[4], 10) || 0,
    };
  }

  /**
   * Mount a live rating: an Alpine `productCardReviews` component that pools
   * the Trustpilot totals for the SKU list, the same way a product card does.
   * Static snapshots only carry the metafield fallbacks, which are 0 for
   * products whose rating comes from Trustpilot alone.
   * @param {HTMLElement} ratingsHost
   * @param {{ sku: string, rating: number, count: number } | null} args
   * @returns {boolean} true when the live rating was mounted
   */
  #mountLiveRatings(ratingsHost, args) {
    if (!args || !args.sku) return false;
    const template = this.refs.ratingsLiveTemplate;
    if (!(template instanceof HTMLTemplateElement)) return false;
    const root = template.content.firstElementChild;
    if (!(root instanceof HTMLElement)) return false;

    const clone = /** @type {HTMLElement} */ (root.cloneNode(true));
    clone.setAttribute(
      "x-data",
      `productCardReviews(${JSON.stringify(args.sku)}, ${args.rating}, ${args.count})`,
    );

    // Gift products read "4.4 · 19 reviews" like the cut PDP and the builder's
    // Product Details drawer, instead of the card's bare count.
    if (this.#isRechargeGift) {
      const count = clone.querySelector("[x-text]");
      if (count instanceof HTMLElement) {
        count.className = "font-ringside-ssm-light! text-[14px]! text-[#212721]!";
        count.setAttribute(
          "x-text",
          "`${rating.toFixed(1)} · ${reviewCount.toLocaleString()} reviews`",
        );
      }
    }

    ratingsHost.appendChild(clone);
    return true;
  }

  #renderRatings(ratingsHost, ratingsEl) {
    // Prefer a live rating so the drawer shows the pooled Trustpilot total
    // even when the card's own fetch has not resolved yet.
    if (this.#mountLiveRatings(ratingsHost, this.#parseReviewsArgs(ratingsEl))) {
      return;
    }

    // For Alpine-based reviews, we need to extract just the visible content
    // to avoid template duplication when Alpine re-initializes on the clone
    const starsContainer = ratingsEl.querySelector(
      ".product-card-reviews-stars",
    );
    const countEl =
      ratingsEl.querySelector("[x-text]") ||
      ratingsEl.querySelector("span:last-child");

    if (!starsContainer) {
      // Fallback: just clone the whole thing (static ratings markup)
      ratingsHost.appendChild(ratingsEl.cloneNode(true));
      return;
    }

    // Create a static snapshot of the ratings
    const wrapper = document.createElement("div");
    wrapper.className = "!flex !items-center !gap-1";

    // Clone only the actual star spans (not the template tags)
    const starsClone = document.createElement("div");
    starsClone.className = starsContainer.className;
    starsClone.setAttribute("role", "img");
    starsClone.setAttribute(
      "aria-label",
      starsContainer.getAttribute("aria-label") || "",
    );

    // Only copy actual span elements, skip template tags
    for (const child of starsContainer.children) {
      if (child.tagName === "SPAN") {
        starsClone.appendChild(child.cloneNode(true));
      }
    }

    wrapper.appendChild(starsClone);

    // Add the count
    if (countEl) {
      const countClone = document.createElement("span");
      countClone.className = "!text-sm !text-gray-1100";
      countClone.textContent = countEl.textContent || "";
      wrapper.appendChild(countClone);
    }

    ratingsHost.appendChild(wrapper);
  }

  /**
   * @param {HTMLElement} titleHost
   * @param {Element | null} titleEl
   * @param {HTMLElement} card
   */
  #renderTitle(titleHost, titleEl, card) {
    const text = (
      titleEl?.textContent ||
      card.querySelector(".product-card__link .visually-hidden")?.textContent ||
      ""
    )
      .replace(/\s+/g, " ")
      .trim();

    if (!text) return;

    /** @type {string | null} */
    let href = null;
    if (titleEl instanceof HTMLAnchorElement)
      href = titleEl.getAttribute("href");
    if (!href) {
      const cardLink = card.querySelector('[ref="productCardLink"]');
      if (cardLink instanceof HTMLAnchorElement)
        href = cardLink.getAttribute("href");
    }

    const link = document.createElement("a");
    link.className = "quick-view-drawer__title-link";
    link.textContent = text;
    if (href) link.href = href;

    titleHost.append(link);
  }

  handleViewDetails() {
    const formHost = this.refs.formHost;
    const formComponent =
      formHost instanceof HTMLElement
        ? formHost.querySelector("product-form-component")
        : null;

    const productUrl =
      (formComponent instanceof HTMLElement &&
        formComponent.dataset.productUrl) ||
      this.#currentProductUrl;
    if (!productUrl) return;

    const idInput =
      formComponent instanceof HTMLElement
        ? formComponent.querySelector('input[name="id"]')
        : null;
    const variantId =
      idInput instanceof HTMLInputElement ? idInput.value : null;

    try {
      const url = new URL(productUrl, window.location.origin);
      if (variantId) url.searchParams.set("variant", variantId);
      window.location.assign(url.toString());
    } catch {
      window.location.assign(productUrl);
    }
  }

  /**
   * Apply gift-box specific styling - hide subscription options, satisfaction guarantee, and view details
   */
  #applyGiftBoxStyles() {
    if (!this.#isGiftBoxQuickView) return;

    // Hide view details button
    const viewDetailsWrapper = this.refs.viewDetailsWrapper;
    if (viewDetailsWrapper instanceof HTMLElement) {
      viewDetailsWrapper.style.display = "none";
    }

    // Hide the satisfaction guarantee section
    const regularSatisfaction = this.refs.regularSatisfactionGuarantee;
    if (regularSatisfaction instanceof HTMLElement) {
      regularSatisfaction.style.display = "none";
    }
  }
}

if (!customElements.get("quick-view-drawer")) {
  customElements.define("quick-view-drawer", QuickViewDrawer);
}
